import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import axios from '../../../api/axios';
import { BusinessCategory1, BusinessCategory2, BusinessCategory3 } from '../../../types';

interface BusinessCategorySelectorProps {
  allCategory1Data: BusinessCategory1[];
  allCategory2Data: BusinessCategory2[];
  allCategory3Data: BusinessCategory3[];
  category1Value: number;
  category2Value: number;
  category3Value: number;
  onCategory1Change: (value: number) => void;
  onCategory2Change: (value: number) => void;
  onCategory3Change: (value: number) => void;
  errors?: {
    business_category1_id?: string;
    business_category2_id?: string;
    business_category3_id?: string;
  };
}

const BusinessCategorySelector: React.FC<BusinessCategorySelectorProps> = ({
  allCategory1Data,
  allCategory2Data,
  allCategory3Data,
  category1Value,
  category2Value,
  category3Value,
  onCategory1Change,
  onCategory2Change,
  onCategory3Change,
  errors = {}
}) => {
  const [category2Options, setCategory2Options] = useState<BusinessCategory2[]>([]);
  const [category3Options, setCategory3Options] = useState<BusinessCategory3[]>([]);

  useEffect(() => {
    if (allCategory2Data.length > 0 && category1Value) {
      const filteredCategory2 = allCategory2Data.filter(
        cat => cat.business_category1_id === category1Value
      );
      setCategory2Options(filteredCategory2);
    } else if (!category1Value) {
      setCategory2Options([]);
    }
  }, [allCategory2Data, category1Value]);

  useEffect(() => {
    if (allCategory3Data.length > 0 && category1Value && category2Value) {
      const filteredCategory3 = allCategory3Data.filter(
        cat => cat.business_category1_id === category1Value && cat.business_category2_id === category2Value
      );
      setCategory3Options(filteredCategory3);
    } else if (!category1Value || !category2Value) {
      setCategory3Options([]);
    }
  }, [allCategory3Data, category1Value, category2Value]);

  const handleCategory1Change = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value) || 0;
    onCategory1Change(value);
  };

  const handleCategory2Change = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value) || 0;
    onCategory2Change(value);
  };

  const handleCategory3Change = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value) || 0;
    onCategory3Change(value);
  };

  return (
    <>
      <Form.Select
        value={category1Value}
        onChange={handleCategory1Change}
        isInvalid={!!errors.business_category1_id}
        className="form-select mb-2"
      >
        <option value="">大分類を選択してください</option>
        {allCategory1Data.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </Form.Select>
      {errors.business_category1_id && (
        <Form.Control.Feedback type="invalid">
          {errors.business_category1_id}
        </Form.Control.Feedback>
      )}

      <Form.Select
        value={category2Value}
        onChange={handleCategory2Change}
        isInvalid={!!errors.business_category2_id}
        className="form-select mb-2"
        disabled={!category1Value || category2Options.length === 0}
      >
        <option value="">中分類を選択してください</option>
        {category2Options.map((option) => (
          <option key={`${option.business_category1_id}-${option.id}`} value={option.id}>
            {option.name}
          </option>
        ))}
      </Form.Select>
      {errors.business_category2_id && (
        <Form.Control.Feedback type="invalid">
          {errors.business_category2_id}
        </Form.Control.Feedback>
      )}

      <Form.Select
        value={category3Value}
        onChange={handleCategory3Change}
        isInvalid={!!errors.business_category3_id}
        className="form-select"
        disabled={!category2Value || category3Options.length === 0}
      >
        <option value="">小分類を選択してください</option>
        {category3Options.map((option) => (
          <option key={`${option.business_category1_id}-${option.business_category2_id}-${option.id}`} value={option.id}>
            {option.name}
          </option>
        ))}
      </Form.Select>
      {errors.business_category3_id && (
        <Form.Control.Feedback type="invalid">
          {errors.business_category3_id}
        </Form.Control.Feedback>
      )}
    </>
  );
};

export default BusinessCategorySelector;
